/* "Real cost of an employee" calculator on the marketing home (prototype homeCalc),
 * computed with the same domain functions the product uses (gosiOf, eosb). Saudi Arabia by default; on another GCC
 * country's home page the country's social insurance and end-of-service rules apply ("old" = a citizen, "ns" = an
 * expatriate; the Saudi-only "new" GOSI regime is not offered there). */
import type { Employee, GosiSettings } from "../../data/types";
import { addDays } from "../../domain/dates";
import { countryOf, siDefaults } from "../../domain/country";
import { annuityRateNew, eosb, gosiOf } from "../../domain/payroll";

/** Published GOSI defaults (same as seed settings.gosi). */
export const DEFAULT_GOSI: GosiSettings = { saEmpAnn: 9, saEmpSaned: 0.75, saErAnn: 9, saErSaned: 0.75, saErOH: 2, nsErOH: 2, cap: 45000 };
export type CalcNat = "new" | "old" | "ns";
export interface HomeCalcInput { basic: number; nat: CalcNat; yrs: number }

const pct = (n: number) => String(Math.round(n * 100) / 100) + "%";

export function homeCalc(c: HomeCalcInput, today: string, country = "SA") {
  if (country !== "SA") return homeCalcIn(c, today, country);
  const b = Number.isFinite(c.basic) && c.basic > 0 ? c.basic : 0;
  const ho = Math.round(b * 0.25), tr = Math.round(b * 0.1);
  const joined = addDays(today, -Math.round((c.yrs || 0) * 365.2425));
  const e: Employee = {
    id: 0, code: "", nameEn: "", nameAr: "", nationality: c.nat === "ns" ? "IN" : "SA", gender: "M", dept: "", title: "", branch: "",
    basic: b, housing: ho, transport: tr, other: 0, joined, status: "Active", idNumber: "", mobile: "", email: "", bank: "", iban: "", dob: "",
    gosiRegime: c.nat === "new" ? "new" : "legacy", gosiWage: b + ho, qiwa: { contractNo: "", status: "", basic: b }, disabled: false, probationEnd: null,
    cchiClass: "", dependants: [], ticket: null, iqamaExpiry: null, documents: [],
  };
  const g = gosiOf(e, DEFAULT_GOSI, today);
  const eo = eosb(e, today, "termination");
  const ann = annuityRateNew(today, DEFAULT_GOSI.saEmpAnn);
  const rate = c.nat === "ns" ? "0% / 2%" : c.nat === "new" ? (ann + 0.75).toFixed(2) + "% / " + (ann + 2.75).toFixed(2) + "%" : "9.75% / 11.75%";
  const gross = b + ho + tr;
  return { ho, tr, gross, gEmp: g.emp, gEr: g.er, net: gross - g.emp, cost: gross + g.er, eos: eo.award, rate };
}

/** Another GCC country: citizens pay the country's pension rates, expatriates only the non-citizen rates. */
function homeCalcIn(c: HomeCalcInput, today: string, country: string) {
  const ci = countryOf(country);
  const g = siDefaults(country);
  const b = Number.isFinite(c.basic) && c.basic > 0 ? c.basic : 0;
  const ho = Math.round(b * 0.25), tr = Math.round(b * 0.1);
  const joined = addDays(today, -Math.round((c.yrs || 0) * 365.2425));
  const citizen = c.nat !== "ns";
  const e: Employee = {
    id: 0, code: "", nameEn: "", nameAr: "", nationality: citizen ? ci.code : "IN", gender: "M", dept: "", title: "", branch: "",
    basic: b, housing: ho, transport: tr, other: 0, joined, status: "Active", idNumber: "", mobile: "", email: "", bank: "", iban: "", dob: "",
    gosiRegime: "legacy", gosiWage: b + ho, qiwa: { contractNo: "", status: "", basic: b }, disabled: false, probationEnd: null,
    cchiClass: "", dependants: [], ticket: null, iqamaExpiry: null, documents: [],
  };
  const s = gosiOf(e, g, today, country);
  const eo = eosb(e, today, "termination", undefined, country);
  const rate = s.base > 0 ? pct((s.emp / s.base) * 100) + " / " + pct((s.er / s.base) * 100)
    : citizen ? pct(g.saEmpAnn + g.saEmpSaned) + " / " + pct(g.saErAnn + g.saErSaned + g.saErOH) : pct(g.nsEmp ?? 0) + " / " + pct(g.nsErOH);
  const gross = b + ho + tr;
  return { ho, tr, gross, gEmp: s.emp, gEr: s.er, net: gross - s.emp, cost: gross + s.er, eos: eo.award, rate };
}
